/**
 * Portal Manta · Aluci-Guard (Cap 16.3 · Pacote A)
 * Validador anti-alucinação · ABNT + Leis + SICRO + URLs
 * v29 modular · 2026-05-11
 */
import React, { useState, useCallback, memo } from 'react';
import { M, F } from '../../tokens/theme.js';
import { STATUS } from '../../tokens/colors.js';

const ALUCI_REG_ABNT_MIN = new Set([
  "6118","6122","6123","8681","7187","7188","8800","6484","6489",
  "6457","6459","7180","7181","7182","9050","10004","10520","6023","12655",
  "14931","15575","16697","7211","13133","14166","15495","11906","14541"
]);
const ALUCI_REG_LEIS_MIN = new Set([
  "8987/1995","9074/1995","11079/2004","13334/2016","13448/2017",
  "14133/2021","8666/1993","10520/2002","10233/2001","12379/2011",
  "6938/1981","9605/1998","12305/2010","12651/2012","9985/2000",
  "13848/2019","12846/2013","12527/2011","13709/2018"
]);

const AluciGuardPlaceholderPage = memo(function AluciGuardPlaceholderPage_Inner(){
  const [texto, setTexto] = useState('');
  const [achados, setAchados] = useState(null);

  const auditar = useCallback(() => {
    if (!texto || !texto.trim()) {
      setAchados({total:0, ok:0, naoLoc:0, lista:[], aviso:'Texto vazio.'});
      return;
    }
    const out = [];
    // ABNT
    const rxAbnt = /\bABNT\s*NBR\s*(\d+)(?:[\s:./\-](\d{2,4}))?/gi;
    let m;
    while ((m = rxAbnt.exec(texto)) !== null) {
      const num = m[1];
      const linha = texto.slice(0, m.index).split('\n').length;
      out.push({
        tipo:'ABNT', match:m[0], num, linha,
        status: ALUCI_REG_ABNT_MIN.has(num) ? 'OK' : 'NÃO LOCALIZADO',
      });
    }
    // Leis
    const rxLei = /\bLei\s*(?:federal\s+)?n?[º°.\s]*\s*(\d{1,5}(?:\.\d{3})?)\s*[,/]\s*(\d{2,4})/gi;
    while ((m = rxLei.exec(texto)) !== null) {
      const num = m[1].replace(/\./g,'');
      const ano = m[2].length === 2 ? (parseInt(m[2],10) > 50 ? '19'+m[2] : '20'+m[2]) : m[2];
      const chave = `${num}/${ano}`;
      const linha = texto.slice(0, m.index).split('\n').length;
      out.push({
        tipo:'LEI', match:m[0], chave, linha,
        status: ALUCI_REG_LEIS_MIN.has(chave) ? 'OK' : 'NÃO LOCALIZADO',
      });
    }
    // SICRO
    const rxSicro = /\bSICRO\s*[\s\-:]\s*(\d{6,8})/gi;
    while ((m = rxSicro.exec(texto)) !== null) {
      const cod = m[1];
      const linha = texto.slice(0, m.index).split('\n').length;
      out.push({
        tipo:'SICRO', match:m[0], cod, linha,
        status: /^\d{7,8}$/.test(cod) ? 'ESTRUTURA OK' : 'INVÁLIDO',
      });
    }
    // URL
    const rxUrl = /\bhttps?:\/\/[^\s\)<>\]\}\"\']+/g;
    while ((m = rxUrl.exec(texto)) !== null) {
      const linha = texto.slice(0, m.index).split('\n').length;
      out.push({
        tipo:'URL', match:m[0].slice(0,60), linha,
        status: 'ESTRUTURA OK (não verificada online)',
      });
    }

    const ok = out.filter(a => a.status === 'OK' || a.status.startsWith('ESTRUTURA OK')).length;
    const naoLoc = out.filter(a => a.status === 'NÃO LOCALIZADO' || a.status === 'INVÁLIDO').length;
    setAchados({total:out.length, ok, naoLoc, lista:out, aviso:null});
  }, [texto]);

  return (<div style={{padding:'18px 24px', maxWidth:1100, margin:'0 auto', fontFamily:F.body}}>
    <div style={{marginBottom:14, padding:'14px 18px', background:'linear-gradient(135deg, #1F2937 0%, #374151 100%)',
      color:'#fff', borderRadius:6, borderBottom:`3px solid ${M.accent}`}}>
      <div style={{display:'inline-block', padding:'3px 10px', background:M.accent, color:'#fff',
        fontFamily:F.mono, fontSize:10, fontWeight:700, letterSpacing:1.5, borderRadius:3, marginBottom:8}}>
        CAP 16.3 · PACOTE A · v0.2.0
      </div>
      <div style={{fontFamily:F.display, fontSize:20, fontWeight:600}}>🛡 Aluci-Guard · Validador Anti-Alucinação</div>
      <div style={{fontSize:11.5, color:'#D1D5DB', marginTop:4}}>
        Detecta normas ABNT, leis federais, códigos SICRO e URLs em qualquer texto. Confronta contra registry local.
      </div>
      <div style={{marginTop:8, fontFamily:F.mono, fontSize:10, color:'#FCD34D'}}>
        Registry: {ALUCI_REG_ABNT_MIN.size} normas ABNT · {ALUCI_REG_LEIS_MIN.size} leis federais · SICRO via regex estrutural
      </div>
    </div>

    <div style={{marginBottom:14, padding:'12px 14px', background:M.bgAlt, border:`1px solid ${M.borderSoft}`,
      borderRadius:4, fontSize:11.5, color:M.muted, lineHeight:1.5}}>
      <b style={{color:M.title}}>Como usar:</b> cole o texto de um laudo, claim, parecer ou GR e clique <b>AUDITAR</b>.
      O sistema detecta padrões (ABNT NBR, Lei n°, SICRO, URLs) e marca cada um como <b>OK</b> (encontrado no registry)
      ou <b>NÃO LOCALIZADO</b> (suspeito de alucinação · fail-loud Cap 16).
    </div>

    <textarea
      value={texto}
      onChange={e=>setTexto(e.target.value)}
      placeholder="Cole aqui o texto a auditar... &#10;&#10;Exemplo: 'O presente laudo observa a ABNT NBR 6118:2023 e a Lei nº 14.133/2021. Composição SICRO 4011582 aplicada.'"
      style={{
        width:'100%', minHeight:200, padding:14, border:`2px solid ${M.border}`,
        borderRadius:6, fontSize:12, fontFamily:F.mono, lineHeight:1.55,
        background:'#FAFBFC', color:M.text, marginBottom:12,
      }}
    />

    <div style={{display:'flex', gap:10, marginBottom:18}}>
      <button onClick={auditar} disabled={!texto.trim()} style={{
        padding:'10px 24px', background: texto.trim() ? M.accent : '#E5E7EB',
        color:'#fff', border:'none', borderRadius:4,
        fontFamily:F.mono, fontSize:12, fontWeight:700, letterSpacing:.6,
        cursor: texto.trim() ? 'pointer' : 'not-allowed',
      }}>🛡 AUDITAR</button>
      <button onClick={()=>{ setTexto(''); setAchados(null); }} style={{
        padding:'10px 16px', background:'transparent', color:M.muted,
        border:`1px solid ${M.border}`, borderRadius:4,
        fontFamily:F.mono, fontSize:11, fontWeight:600, cursor:'pointer',
      }}>LIMPAR</button>
    </div>

    {achados && (<div style={{background:'#fff', border:`1px solid ${M.border}`, borderRadius:6, overflow:'hidden'}}>
      <div style={{padding:'10px 14px', background:M.title, color:'#fff',
        borderBottom:`2px solid ${M.accent}`, display:'flex', alignItems:'center', gap:12}}>
        <span style={{fontFamily:F.display, fontSize:14, fontWeight:600}}>Resultado da Auditoria</span>
        <span style={{flex:1}}></span>
        <span style={{fontFamily:F.mono, fontSize:11, color:'#FCD34D'}}>
          {achados.total} padrões · {achados.ok} OK · {achados.naoLoc} não localizados
        </span>
      </div>

      {achados.aviso && (<div style={{padding:'14px 18px', fontSize:11.5, color:M.muted, fontStyle:'italic'}}>{achados.aviso}</div>)}

      {achados.lista.length > 0 && (<table style={{width:'100%', borderCollapse:'collapse', fontSize:11}}>
        <thead><tr>
          <th style={{padding:'7px 10px', background:M.bgAlt, color:M.muted, fontFamily:F.mono, fontSize:10, letterSpacing:.5, textAlign:'left'}}>L</th>
          <th style={{padding:'7px 10px', background:M.bgAlt, color:M.muted, fontFamily:F.mono, fontSize:10, letterSpacing:.5, textAlign:'left'}}>TIPO</th>
          <th style={{padding:'7px 10px', background:M.bgAlt, color:M.muted, fontFamily:F.mono, fontSize:10, letterSpacing:.5, textAlign:'left'}}>MATCH</th>
          <th style={{padding:'7px 10px', background:M.bgAlt, color:M.muted, fontFamily:F.mono, fontSize:10, letterSpacing:.5, textAlign:'left'}}>STATUS</th>
        </tr></thead>
        <tbody>{achados.lista.map((a,i) => {
          const corStatus = a.status === 'OK' ? '#10B981' : a.status.startsWith('ESTRUTURA') ? '#3B82F6' : '#EF4444';
          const bgStatus = a.status === 'OK' ? '#D1FAE5' : a.status.startsWith('ESTRUTURA') ? '#DBEAFE' : '#FECACA';
          return (<tr key={i} style={{borderTop:`1px solid ${M.borderSoft}`}}>
            <td style={{padding:'6px 10px', fontFamily:F.mono, fontSize:10, color:M.muted}}>{a.linha}</td>
            <td style={{padding:'6px 10px', fontFamily:F.mono, fontSize:10, color:M.title, fontWeight:700}}>{a.tipo}</td>
            <td style={{padding:'6px 10px', fontFamily:F.mono, fontSize:11, color:M.text}}>{a.match}</td>
            <td style={{padding:'6px 10px'}}>
              <span style={{padding:'2px 8px', background:bgStatus, color:corStatus,
                fontFamily:F.mono, fontSize:9.5, fontWeight:700, letterSpacing:.3, borderRadius:3}}>{a.status}</span>
            </td>
          </tr>);
        })}</tbody>
      </table>)}
    </div>)}

    <div style={{marginTop:18, padding:'10px 14px', background:'#EFF6FF', border:`1px solid #93C5FD`, borderRadius:4,
      fontSize:11, color:'#1E3A8A', lineHeight:1.5}}>
      <b>📚 Versão completa:</b> esta é a versão JavaScript embutida no portal · cobertura básica (29 ABNT + 19 Leis + SICRO estrutural).
      A versão Python completa (44 ABNT + 31 Leis + 20 SICRO + Excel export + DOI/URL online check) está em <code>/home/claude/aluci-guard/</code>.
      Para o registry expandido (~500 ABNT + leis estaduais), aguardar Pacote B & C (P10, P11).
    </div>
  </div>);
});

export { AluciGuardPlaceholderPage };
export default AluciGuardPlaceholderPage;
